import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper';

const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';

interface LastUpdateData {
  lastUpdated: string | null;
  recordCount: number;
  services: string[];
  status: string;
}

interface PriceChange {
  sku_id: string;
  service_name: string;
  old_price: number;
  new_price: number;
  currency: string;
  changed_at: string;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function isStale(iso: string | null): boolean {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > 25 * 60 * 60 * 1000;
}

function pctChange(oldPrice: number, newPrice: number): string {
  if (oldPrice === 0) return '—';
  const pct = ((newPrice - oldPrice) / oldPrice) * 100;
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export default function PriceHistoryPage() {
  const [data, setData] = useState<LastUpdateData | null>(null);
  const [changes, setChanges] = useState<PriceChange[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${apiBase}/api/prices/refresh-log`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: LastUpdateData) => setData(d))
      .catch(() => setError(true));

    fetch(`${apiBase}/api/prices/history?limit=50`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: { changes: PriceChange[] }) => setChanges(d.changes))
      .catch(() => setChanges([]));
  }, []);

  const stale = isStale(data?.lastUpdated ?? null);

  return (
    <div className="px-4 py-8 max-w-[760px] mx-auto">
      <Helmet>
        <title>Azure Price History | AzureCalc.uk</title>
        <meta name="description" content="Recent Azure price changes for UK South in GBP — tracked nightly from the Azure Retail Prices API." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/history" />
      </Helmet>

      <Link to="/" className="inline-flex items-center gap-1 text-[12px] mb-6 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
        Back to calculators
      </Link>

      <h1 className="text-[26px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Price History</h1>
      <p className="text-[14px] mb-8 leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
        Azure retail price movements detected during each nightly refresh. UK South · GBP.
      </p>

      {/* Status card */}
      {error ? (
        <div className="rounded-lg p-5 mb-6 text-[13px] bg-[#a4262c]/10 border border-[#a4262c]/40" style={{ color: 'var(--text-tertiary)' }}>
          Unable to load price status. Prices are still available.
        </div>
      ) : (
        <div className="rounded-lg p-5 mb-8 border"
          style={data === null
            ? { backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }
            : stale
              ? { backgroundColor: 'var(--surface-container)', borderColor: 'rgba(232, 176, 0, 0.6)' }
              : { backgroundColor: 'var(--surface-container)', borderColor: 'rgba(16, 124, 16, 0.6)' }}>
          {data === null ? (
            <div className="flex flex-col gap-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-3 rounded animate-pulse w-1/2" style={{ backgroundColor: 'var(--surface-container-high)' }} />)}
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Price Data Status</p>
                <span className={`text-[12px] font-semibold ${stale ? 'text-[#e8b000]' : 'text-[#107c10]'}`}>
                  {stale ? '⚠️ Stale' : '✅ Current'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
                <div>
                  <span style={{ color: 'var(--text-tertiary)' }}>Last updated</span>
                  <p className="font-semibold mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {data.lastUpdated ? formatDateTime(data.lastUpdated) : '—'}
                  </p>
                </div>
                <div>
                  <span style={{ color: 'var(--text-tertiary)' }}>Prices cached</span>
                  <p className="font-semibold mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {data.recordCount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <span style={{ color: 'var(--text-tertiary)' }}>Source</span>
                  <p className="mt-0.5" style={{ color: 'var(--text-secondary)' }}>Azure Retail Prices API · UK South · GBP</p>
                </div>
                <div>
                  <span style={{ color: 'var(--text-tertiary)' }}>Schedule</span>
                  <p className="mt-0.5" style={{ color: 'var(--text-secondary)' }}>Nightly at 02:00 UTC</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Price change history */}
      <div className="mb-8">
        <CalculatorContentWrapper
          eyebrow="Pricing Insights"
          title="Azure price history is a leading indicator for architecture decisions"
          intro={
            <>
              <p className="mb-3">
                Azure prices change regularly, but most teams only notice when they receive the invoice
                and the charges are higher than expected. Price History lets you spot movements early: when
                a service you're considering increases in cost, you might opt for an alternative architecture.
                When a service decreases (rare but it happens), it might be time to repatriate workloads you
                previously moved away. This page shows the 50 most recent price movements detected during nightly
                refreshes so you can stay ahead of cost surprises.
              </p>
              <p>
                The history is not a prediction tool or a negotiation aid. It's a transparency layer that helps
                you understand Microsoft's pricing dynamic for the services you depend on, and signals when design
                trade-offs should be reconsidered.
              </p>
            </>
          }
          sections={[
            {
              id: 'why-price-history-matters',
              title: 'Why price history matters for your architecture',
              body: (
                <>
                  <p className="mb-3">
                    Price changes signal market shifts and priority changes inside Microsoft. A price drop
                    usually means increased competition or strategic push for that service. A price increase
                    might indicate demand outpacing capacity or a deliberate pivot away from commoditization.
                    Either way, your cost model becomes stale as soon as the price moves, and your historical
                    estimates are no longer representative.
                  </p>
                  <p>
                    For teams forecasting three years ahead, a 10% price increase on a core service (e.g., Blob
                    Storage or Functions) might flip a cost-benefit analysis. For teams mid-migration, a competitor
                    service dropping in price might justify rethinking architecture. Price history lets you spot
                    these shifts as they happen, not three months later when the invoice arrives.
                  </p>
                </>
              ),
            },
            {
              id: 'how-captures-work',
              title: 'How price captures work: nightly snapshots',
              body: (
                <>
                  <p className="mb-3">
                    Every night at 02:00 UTC, a refresh job queries the Azure Retail Prices API for all meters
                    in UK South, GBP. It compares the fresh prices against the previous day's snapshot. If any
                    meter has moved (gone up, down, or was added/removed), it's recorded as a change.
                  </p>
                  <p>
                    This means: you'll see changes within 24 hours of Microsoft updating prices. The site doesn't
                    predict or interpolate; it only records actual movements detected during the refresh. If no
                    changes happened overnight, the history is silent until the next movement. This avoids false
                    positives and keeps the change log clean.
                  </p>
                </>
              ),
            },
            {
              id: 'interpreting-changes',
              title: 'How to read and interpret a price change',
              body: (
                <>
                  <p className="mb-3">
                    Each row in the history table shows:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-3">
                    <li><strong>Service:</strong> Which Azure product (e.g., Storage, Functions, Log Analytics)</li>
                    <li><strong>SKU:</strong> The specific meter or unit being billed (e.g., "Hot Blob Storage" or "Standard Compute vCore-hours")</li>
                    <li><strong>Old / New:</strong> The previous and current per-unit price in GBP</li>
                    <li><strong>Change:</strong> The percentage movement. Positive = price increase; negative = price decrease</li>
                    <li><strong>Date:</strong> When the change was first detected</li>
                  </ul>
                  <p>
                    A +5% change on a high-volume meter (e.g., Blob Storage retrieval, which a large analytics
                    workload might do terabytes of daily) could swing from a £50/month line item to £52.50.
                    Multiply across multiple meters or services, and a 5% increase across the board is a real
                    budget impact.
                  </p>
                </>
              ),
            },
            {
              id: 'worked-example',
              title: 'Worked example: spotting a price increase and responding',
              body: (
                <>
                  <p className="mb-3">
                    Scenario: You run a data pipeline that pulls 5 TB of data from Blob Storage Cool tier every day.
                    Your current annual cost: (5 TB × 30 days × 12 months) × £0.015/GB retrieval rate = ~£27,000/year.
                  </p>
                  <p className="mb-3">
                    On 15 March, the price history shows "Cool Blob Retrieval" increased from £0.015/GB to £0.016/GB
                    (+6.7%). Your new annual cost: ~£28,800/year. That's £1,800 extra.
                  </p>
                  <p className="mb-3">
                    You now have four options:
                  </p>
                  <ol className="list-decimal pl-5 space-y-2 mb-3">
                    <li>Accept the increase and raise your budget </li>
                    <li>Optimize the pipeline: reduce retrieval volume by 10%, landing you under the old effective cost</li>
                    <li>Move to Archive tier (cheaper per GB but has restore costs); recalculate if it's worthwhile given your access pattern</li>
                    <li>Explore a competing service: maybe BigQuery or Databricks is now more competitive than Blob Storage for this workload</li>
                  </ol>
                  <p>
                    You would never have known to consider these options if the price change wasn't visible.
                    That visibility is what price history provides.
                  </p>
                </>
              ),
            },
            {
              id: 'what-to-do',
              title: 'What to do when you spot a price change',
              body: (
                <>
                  <p className="mb-3">
                    <strong>1. Identify which services consume that meter.</strong> The SKU tells you which service changed,
                    but not which of your workloads are exposed. A Functions price increase affects all your Functions;
                    a specific storage tier increase is narrower.
                  </p>
                  <p className="mb-3">
                    <strong>2. Recalculate affected workload costs.</strong> Use the calculator to re-estimate your
                    monthly bill with the new rate. Multiply by your expected annual usage to assess the impact.
                  </p>
                  <p className="mb-3">
                    <strong>3. Weigh alternatives.</strong> If the impact is significant (e.g., 10%+ of that service's
                    monthly bill), consider whether another architecture would be cheaper now. High-change scenarios
                    (price increase + low utilization) might be candidates for immediate refactoring.
                  </p>
                  <p>
                    <strong>4. Monitor for reversals.</strong> Occasionally prices drop (rare, but it happens).
                    If a service you moved away from drops in price, revisit whether repatriation makes sense.
                  </p>
                </>
              ),
            },
            {
              id: 'limitations',
              title: 'Limitations and caveats',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Regional variance:</strong> This page tracks UK South only. Other regions may have different
                    price movements (regions usually move together, but not always).
                  </p>
                  <p className="mb-3">
                    <strong>Discounts and commitments not reflected:</strong> Published prices don't account for EA discounts,
                    reserved instances, or commitment plans. A price increase in the published table might be offset by
                    your reservation, or vice versa.
                  </p>
                  <p className="mb-3">
                    <strong>New meters are added, old ones removed:</strong> Azure regularly introduces new offerings.
                    If a new meter appears in the history, it's usually a new service or a new SKU variant, not a price
                    change on existing usage.
                  </p>
                  <p>
                    <strong>No prediction:</strong> Price history is historical data only. We don't forecast future movements
                    or suggest whether a price increase signals a trend or is a one-off adjustment.
                  </p>
                </>
              ),
            },
          ]}
          methodologyNote={
            <>
              <p className="mb-2">
                Prices are captured nightly from the Azure Retail Prices API for UK South in GBP.
                Changes are detected by comparing snapshots. The 50 most recent movements are displayed below.
              </p>
              <Link to="/methodology" className="text-[#0078d4] hover:underline">
                How prices are calculated →
              </Link>
            </>
          }
          relatedLinks={[
            { label: 'Azure Blob Storage Calculator', to: '/blob-storage' },
            { label: 'Azure Functions Calculator', to: '/functions' },
            { label: 'Methodology', to: '/methodology' },
            { label: 'Home', to: '/' },
          ]}
        />
      </div>

      {/* Price change history */}
      <div className="mb-8">
        <h2 className="text-[18px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Recent Price Movements</h2>
        <p className="text-[12px] mb-4" style={{ color: 'var(--text-tertiary)' }}>
          The 50 most recent price changes detected during nightly refreshes.
        </p>

        {changes === null && (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 rounded animate-pulse" style={{ backgroundColor: 'var(--surface-container)' }} />
            ))}
          </div>
        )}

        {changes !== null && changes.length === 0 && (
          <div className="rounded-lg p-6 text-center" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              No price changes recorded yet. Changes will appear here after the next nightly refresh detects a movement.
            </p>
          </div>
        )}

        {changes !== null && changes.length > 0 && (
          <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <table className="w-full text-[12px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Service</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide hidden sm:table-cell" style={{ color: 'var(--text-tertiary)' }}>SKU</th>
                  <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Old</th>
                  <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>New</th>
                  <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Change</th>
                  <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wide hidden md:table-cell" style={{ color: 'var(--text-tertiary)' }}>Date</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--surface-container-high)' }}>
                {changes.map((c, i) => {
                  const up = c.new_price > c.old_price;
                  const pct = pctChange(c.old_price, c.new_price);
                  return (
                    <tr key={i} className="transition-colors" style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                      <td className="px-4 py-2.5" style={{ color: 'var(--text-secondary)' }}>{c.service_name}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px] hidden sm:table-cell max-w-[160px] truncate" style={{ color: 'var(--text-tertiary)' }}>{c.sku_id}</td>
                      <td className="px-4 py-2.5 text-right font-mono" style={{ color: 'var(--text-tertiary)' }}>£{c.old_price.toFixed(4)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold" style={{ color: 'var(--text-secondary)' }}>£{c.new_price.toFixed(4)}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${up ? 'text-[#a4262c]' : 'text-[#107c10]'}`}>
                        {pct}
                      </td>
                      <td className="px-4 py-2.5 text-right hidden md:table-cell" style={{ color: 'var(--text-tertiary)' }}>{formatDate(c.changed_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Source link */}
      <div className="flex items-center gap-4">
        <Link to="/methodology" className="text-[#0078d4] hover:underline text-[13px]">
          How prices are calculated →
        </Link>
        <a
          href="https://learn.microsoft.com/en-us/rest/api/cost-management/retail-prices/azure-retail-prices"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#0078d4] hover:underline text-[13px]"
        >
          <span className="material-symbols-outlined text-[14px]">open_in_new</span>
          Azure Retail Prices API docs
        </a>
      </div>
    </div>
  );
}
